import { useState } from "react"
import {
  Search,
  X,
  ChevronLeft,
  ChevronRight,
  RefreshCw,
  Star,
  Archive,
  Trash2,
  Clock,
  MailOpen,
  Settings,
  HelpCircle,
  SlidersHorizontal,
  Sparkles,
  LayoutGrid,
} from "lucide-react"

// ── Design tokens ──────────────────────────────────────────────────────────────
const FF   = "'DM Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"
const MONO = "'IBM Plex Mono', monospace"

const T = {
  border:     "0.5px solid #e5e7eb",
  radius:     8,
  radiusCard: 10,
  bg:         "#fff",
  bgPage:     "#f5f5f0",
  bgMuted:    "#fafaf9",
  bgUnread:   "#eef3fd",
  bgRead:     "#fafaf9",
  text:       "#111827",
  textMuted:  "#374151",
  textTertiary:"#6b7280",
  shadow:     "0 1px 3px rgba(0,0,0,0.06), 0 1px 2px rgba(0,0,0,0.04)",
}

// ── Helpers ────────────────────────────────────────────────────────────────────
export const formatTime = (timestamp) => {
  if (!timestamp) return ""
  const date = timestamp instanceof Date ? timestamp : new Date(timestamp)
  const now  = new Date()
  const diffDays = Math.floor((now - date) / 86_400_000)
  if (diffDays === 0) return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: true })
  if (diffDays === 1) return "Yesterday"
  if (diffDays < 7)  return date.toLocaleDateString([], { weekday: "short" })
  return date.toLocaleDateString([], { month: "short", day: "numeric" })
}

export const stripHtml = (html) => {
  if (!html) return ""
  const d = new DOMParser().parseFromString(html, "text/html")
  return d.body.textContent || ""
}

const getInitials = (name = "") =>
  name.split(" ").filter(Boolean).slice(0, 2).map((w) => w[0].toUpperCase()).join("") || "?"

// ── InterviewsTab-matching avatar gradients ────────────────────────────────────
const AVATAR_GRADIENTS = [
  "linear-gradient(135deg,#6366f1,#8b5cf6)",
  "linear-gradient(135deg,#0ea5e9,#6366f1)",
  "linear-gradient(135deg,#10b981,#0ea5e9)",
  "linear-gradient(135deg,#f59e0b,#ef4444)",
  "linear-gradient(135deg,#ec4899,#8b5cf6)",
  "linear-gradient(135deg,#14b8a6,#6366f1)",
  "linear-gradient(135deg,#f97316,#eab308)",
  "linear-gradient(135deg,#06b6d4,#10b981)",
]
const getAvatarGradient = (name = "") =>
  AVATAR_GRADIENTS[name.split("").reduce((a, c) => a + c.charCodeAt(0), 0) % AVATAR_GRADIENTS.length]
// alias for header profile bg
function getInitialsBg(name = "") { return getAvatarGradient(name) }

// ── Avatar — gradient, rounded square, always initials (matches InterviewsTab) ─
export const Avatar = ({ name = "", size = 32 }) => (
  <div style={{
    width: size, height: size, borderRadius: 7,
    background: getAvatarGradient(name),
    display: "flex", alignItems: "center", justifyContent: "center",
    flexShrink: 0, color: "#fff",
    fontSize: size * 0.38, fontWeight: 700, fontFamily: FF,
    letterSpacing: "0.02em", userSelect: "none",
  }}>
    {getInitials(name)}
  </div>
)

// ── Shimmer rows ───────────────────────────────────────────────────────────────
const RowShimmer = () => (
  <>
    {[1,2,3,4,5,6,7,8].map((i) => (
      <div key={i} style={{ display:"flex", alignItems:"center", height:52, padding:"0 20px", borderBottom:T.border }}>
        <div style={{ width:16, height:16, borderRadius:3, background:"#e9ebee", flexShrink:0, marginRight:14 }} />
        <div style={{ width:16, height:16, borderRadius:3, background:"#e9ebee", flexShrink:0, marginRight:14 }} />
        <div style={{ width:32, height:32, borderRadius:7, background:"#e9ebee", flexShrink:0, marginRight:14 }} />
        <div style={{ width:130, height:12, background:"#e9ebee", borderRadius:4, marginRight:24, flexShrink:0 }} />
        <div style={{ flex:1, height:12, background:"#e9ebee", borderRadius:4, marginRight:24 }} />
        <div style={{ width:72, height:11, background:"#e9ebee", borderRadius:4, flexShrink:0 }} />
      </div>
    ))}
  </>
)

// ── Checkbox — thin, matching star weight ──────────────────────────────────────
const Checkbox = ({ checked, onChange }) => (
  <div
    onClick={(e) => { e.stopPropagation(); onChange(!checked) }}
    style={{
      width: 16, height: 16, flexShrink: 0,
      border: `1.5px solid ${checked ? "#2563eb" : "#9ca3af"}`,
      borderRadius: 3,
      background: checked ? "#2563eb" : "transparent",
      display: "flex", alignItems: "center", justifyContent: "center",
      cursor: "pointer",
      transition: "background 0.1s, border-color 0.1s",
    }}
  >
    {checked && (
      <svg width="9" height="7" viewBox="0 0 9 7" fill="none">
        <path d="M1 3.5L3.2 5.8L8 1" stroke="#fff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    )}
  </div>
)

// ── Single conversation row ────────────────────────────────────────────────────
const ConversationRow = ({ conversation, isInOrganization, organizationId, userId, onClick }) => {
  const [hovered, setHovered] = useState(false)
  const [starred, setStarred] = useState(false)
  const [checked, setChecked] = useState(false)

  const myId = isInOrganization ? organizationId : userId

  const isUnread = conversation.lastMessage?.read === false ||
    !(
      (isInOrganization  && conversation.lastMessage?.senderId === organizationId) ||
      (!isInOrganization && conversation.lastMessage?.senderId === userId) ||
      conversation.lastMessage?.read === true
    )

  // isSentByMe: we sent the last message → show "To Name", else "From Name"
  const isSentByMe = conversation.lastMessage?.senderId === myId

  // The contact's name (other party)
  const contactName = isInOrganization
    ? (conversation.metadata?.candidateName || "Candidate")
    : (conversation.metadata?.organizationName || "Organization")

  const preview = stripHtml(conversation.lastMessage?.text || "")
  const subject = isInOrganization
    ? `Re: Application — ${contactName}`
    : `Re: ${contactName}`
  const time = formatTime(conversation.lastMessageAt?.toDate?.() ?? conversation.lastMessageAt)

  const rowBg = checked
    ? "#dbeafe"
    : hovered
    ? isUnread ? "#dde8fc" : "#f1f0ee"
    : isUnread ? T.bgUnread : T.bgRead

  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: "flex", alignItems: "center",
        height: 52, padding: "0 20px",
        borderBottom: T.border,
        background: rowBg,
        cursor: "pointer",
        transition: "background 0.08s",
        userSelect: "none",
      }}
    >
      {/* Checkbox */}
      <div style={{ marginRight: 14, flexShrink: 0 }}>
        <Checkbox checked={checked} onChange={setChecked} />
      </div>

      {/* Star — same 16px, same 1.5px stroke weight as checkbox border */}
      <div
        onClick={(e) => { e.stopPropagation(); setStarred((s) => !s) }}
        style={{
          width: 16, height: 16, flexShrink: 0, marginRight: 14,
          display: "flex", alignItems: "center", justifyContent: "center",
          color: starred ? "#f59e0b" : "#9ca3af",
          cursor: "pointer",
          transition: "color 0.1s, transform 0.1s",
        }}
        onMouseEnter={(e) => { e.currentTarget.style.transform = "scale(1.15)" }}
        onMouseLeave={(e) => { e.currentTarget.style.transform = "scale(1)"    }}
      >
        <Star size={16} fill={starred ? "#f59e0b" : "none"} strokeWidth={2.2} />
      </div>

      {/* Avatar */}
      <div style={{ marginRight: 14, flexShrink: 0 }}>
        <Avatar name={contactName || ""} size={32} />
      </div>

      {/* Sender — "To Name" or "From Name" based on last message direction */}
      <div style={{ width: 186, flexShrink: 0, marginRight: 20, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", display: "flex", alignItems: "baseline", gap: 4 }}>
        <span style={{
          fontSize: 12, fontWeight: 600,
          color: isSentByMe ? T.textTertiary : (isUnread ? "#1d4ed8" : T.textTertiary),
          fontFamily: FF, flexShrink: 0,
          letterSpacing: "0.01em",
        }}>
          {isSentByMe ? "To" : "From"}
        </span>
        <span style={{
          fontSize: 14, fontWeight: isUnread ? 700 : 600,
          color: isUnread ? "#0f172a" : T.textMuted, fontFamily: FF,
          overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
          letterSpacing: isUnread ? "-0.01em" : "normal",
        }}>
          {contactName}
        </span>
      </div>

      {/* Subject + preview */}
      <div style={{ flex: 1, minWidth: 0, display: "flex", alignItems: "baseline", overflow: "hidden", marginRight: 20 }}>
        <span style={{ fontSize: 13, fontWeight: isUnread ? 700 : 500, color: isUnread ? "#0f172a" : T.text, fontFamily: FF, whiteSpace: "nowrap", flexShrink: 0 }}>
          {subject}
        </span>
        <span style={{ fontSize: 13, fontWeight: isUnread ? 600 : 500, color: isUnread ? "#374151" : T.textTertiary, fontFamily: FF, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flexShrink: 1 }}>
          &nbsp;—&nbsp;{preview || "No message content"}
        </span>
      </div>

      {/* Hover actions OR timestamp */}
      {hovered ? (
        <div onClick={(e) => e.stopPropagation()} style={{ display:"flex", alignItems:"center", gap:3, flexShrink:0, width:88, justifyContent:"flex-end" }}>
          {[{ Icon: Archive, title:"Archive" }, { Icon: Trash2, title:"Delete" }, { Icon: Clock, title:"Snooze" }].map(({ Icon, title }) => (
            <button key={title} title={title} onClick={(e) => e.stopPropagation()}
              style={{ width:26, height:26, display:"flex", alignItems:"center", justifyContent:"center", background:"transparent", border:T.border, borderRadius:T.radius - 2, cursor:"pointer", color:T.textTertiary, outline:"none", transition:"all 0.1s" }}
              onMouseEnter={(e) => { e.currentTarget.style.background="#111827"; e.currentTarget.style.color="#fff"; e.currentTarget.style.borderColor="#111827" }}
              onMouseLeave={(e) => { e.currentTarget.style.background="transparent"; e.currentTarget.style.color=T.textTertiary; e.currentTarget.style.borderColor="#e5e7eb" }}
            >
              <Icon size={11} strokeWidth={1.8} />
            </button>
          ))}
        </div>
      ) : (
        <span style={{ fontSize:13, fontWeight: isUnread ? 700 : 500, color: isUnread ? "#1d4ed8" : T.textTertiary, fontFamily:MONO, flexShrink:0, whiteSpace:"nowrap", width:88, textAlign:"right" }}>
          {time}
        </span>
      )}
    </div>
  )
}

// ── EmailThreads ───────────────────────────────────────────────────────────────
const EmailThreads = ({
  conversations,
  filteredConversations,
  loadingConversations,
  searchQuery,
  setSearchQuery,
  isInOrganization,
  organizationId,
  userId,
  onSelectConversation,
  userData,
}) => {
  const [page, setPage] = useState(1)
  const [searchFocused, setSearchFocused] = useState(false)
  const PAGE_SIZE  = 50
  const totalPages = Math.max(1, Math.ceil(filteredConversations.length / PAGE_SIZE))
  const pageStart  = (page - 1) * PAGE_SIZE
  const pageEnd    = Math.min(pageStart + PAGE_SIZE, filteredConversations.length)
  const pageItems  = filteredConversations.slice(pageStart, pageEnd)

  const displayName = userData?.displayName || userData?.email || ""
  const photoURL    = userData?.photoURL

  const paginationBtn = (Icon, disabled, action) => (
    <button
      disabled={disabled}
      onClick={action}
      style={{
        width: 32, height: 32, border: "none", borderRadius: "50%",
        background: "transparent", cursor: disabled ? "default" : "pointer",
        display: "flex", alignItems: "center", justifyContent: "center",
        color: disabled ? "#d1d5db" : "#5f6368",
        transition: "background 0.12s", outline: "none",
      }}
      onMouseEnter={(e) => { if (!disabled) e.currentTarget.style.background = "#f1f3f4" }}
      onMouseLeave={(e) => { e.currentTarget.style.background = "transparent" }}
    >
      <Icon size={16} strokeWidth={1.8} />
    </button>
  )

  const ghostIconBtn = (children, title) => (
    <button
      title={title}
      style={{
        width: 36, height: 36, border: "none", borderRadius: "50%",
        background: "transparent", cursor: "pointer",
        display: "flex", alignItems: "center", justifyContent: "center",
        color: "#5f6368", transition: "background 0.12s", outline: "none", flexShrink: 0,
      }}
      onMouseEnter={(e) => { e.currentTarget.style.background = "#f1f3f4" }}
      onMouseLeave={(e) => { e.currentTarget.style.background = "transparent" }}
    >
      {children}
    </button>
  )

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:opsz,wght@9..40,400;9..40,500;9..40,600;9..40,700&family=IBM+Plex+Mono:wght@400;500;600&display=swap');
        .et-input { font-family: ${FF}; }
        .et-input::placeholder { color: #80868b; font-weight: 400; }
      `}</style>

      {/* ── Header ──────────────────────────────────────────────────── */}
      <div style={{
        background: "#fff",
        borderBottom: "1px solid #e0e0e0",
        padding: "8px 16px 8px 20px",
        height: 64,
        display: "flex", alignItems: "center", gap: 8,
        flexShrink: 0,
        boxSizing: "border-box",
      }}>

        {/* Search pill — Gmail style, full rounded, no left margin */}
        <div style={{ flex: "0 1 720px", position: "relative" }}>
          {/* Search icon left */}
          <div style={{
            position: "absolute", left: 16, top: "50%", transform: "translateY(-50%)",
            display: "flex", alignItems: "center", pointerEvents: "none", zIndex: 1,
          }}>
            <Search size={18} color="#5f6368" strokeWidth={2} />
          </div>

          <input
            className="et-input"
            type="text"
            placeholder="Search mail"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onFocus={() => setSearchFocused(true)}
            onBlur={() => setSearchFocused(false)}
            style={{
              width: "100%", height: 46,
              paddingLeft: 48, paddingRight: searchQuery ? 96 : 56,
              border: "none",
              borderRadius: 23,
              background: searchFocused ? "#fff" : "rgb(243,243,243)",
              boxShadow: searchFocused ? "0 1px 3px rgba(0,0,0,0.2), 0 0 0 1px rgba(0,0,0,0.08)" : "none",
              fontSize: 16, fontFamily: FF, fontWeight: 400,
              color: "#202124", outline: "none", boxSizing: "border-box",
              transition: "background 0.15s, box-shadow 0.15s",
            }}
          />

          {/* Filter / sliders icon right inside pill */}
          <div style={{
            position: "absolute", right: searchQuery ? 40 : 12, top: "50%", transform: "translateY(-50%)",
            display: "flex", alignItems: "center",
          }}>
            <button style={{
              width: 32, height: 32, border: "none", borderRadius: "50%", background: "transparent",
              cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
              color: "#5f6368", outline: "none", transition: "background 0.12s",
            }}
              onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(0,0,0,0.06)" }}
              onMouseLeave={(e) => { e.currentTarget.style.background = "transparent" }}
              title="Search options"
            >
              <SlidersHorizontal size={16} strokeWidth={1.8} />
            </button>
          </div>

          {/* Clear X */}
          {searchQuery && (
            <button onClick={() => setSearchQuery("")} style={{
              position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)",
              background: "none", border: "none", cursor: "pointer",
              color: "#5f6368", display: "flex", alignItems: "center", padding: 2, outline: "none",
            }}>
              <X size={14} strokeWidth={2} />
            </button>
          )}
        </div>

        {/* Spacer */}
        <div style={{ flex: 1 }} />

        {/* ── Right icons cluster — exactly Gmail order ────────────── */}
        <div style={{ display: "flex", alignItems: "center", gap: 2, flexShrink: 0 }}>

          {/* Help */}
          {ghostIconBtn(<HelpCircle size={20} strokeWidth={1.6} />, "Help")}

          {/* Settings */}
          {ghostIconBtn(<Settings size={20} strokeWidth={1.6} />, "Settings")}

          {/* Upgrade button — Gmail style pill */}
          <button style={{
            height: 36, padding: "0 20px",
            border: "none", borderRadius: 18,
            background: "#c2e7ff",
            color: "#001d35",
            fontSize: 14, fontWeight: 600, fontFamily: FF,
            cursor: "pointer", whiteSpace: "nowrap", flexShrink: 0,
            transition: "background 0.12s", outline: "none",
            letterSpacing: "-0.01em",
          }}
            onMouseEnter={(e) => { e.currentTarget.style.background = "#a8d8f8" }}
            onMouseLeave={(e) => { e.currentTarget.style.background = "#c2e7ff" }}
          >
            Upgrade
          </button>

          {/* Gemini / sparkle */}
          {ghostIconBtn(
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" style={{ color: "#5f6368" }}>
              <path d="M12 2C12 2 12.8 7.2 15.2 9.6C17.6 12 22 12 22 12C22 12 17.6 12 15.2 14.4C12.8 16.8 12 22 12 22C12 22 11.2 16.8 8.8 14.4C6.4 12 2 12 2 12C2 12 6.4 12 8.8 9.6C11.2 7.2 12 2 12 2Z" />
            </svg>,
            "Gemini"
          )}

          {/* Apps grid */}
          {ghostIconBtn(
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" style={{ color: "#5f6368" }}>
              <circle cx="5" cy="5" r="1.8" fill="currentColor" />
              <circle cx="12" cy="5" r="1.8" fill="currentColor" />
              <circle cx="19" cy="5" r="1.8" fill="currentColor" />
              <circle cx="5" cy="12" r="1.8" fill="currentColor" />
              <circle cx="12" cy="12" r="1.8" fill="currentColor" />
              <circle cx="19" cy="12" r="1.8" fill="currentColor" />
              <circle cx="5" cy="19" r="1.8" fill="currentColor" />
              <circle cx="12" cy="19" r="1.8" fill="currentColor" />
              <circle cx="19" cy="19" r="1.8" fill="currentColor" />
            </svg>,
            "Apps"
          )}

        </div>
      </div>

      {/* ── Toolbar row ─────────────────────────────────────────────── */}
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        padding: "4px 20px",
        borderBottom: T.border,
        background: T.bg,
        flexShrink: 0,
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <div style={{ width: 7, height: 7, borderRadius: "50%", background: "#111827", flexShrink: 0 }} />
          <span style={{ fontSize: 12, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.09em", color: T.textTertiary, fontFamily: MONO }}>
            Inbox
          </span>
          <span style={{ fontSize: 12, fontWeight: 600, color: T.textTertiary, background: "#f3f4f6", border: T.border, borderRadius: 5, padding: "1px 7px", fontFamily: MONO }}>
            {filteredConversations.length}
          </span>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 2 }}>
          {/* Refresh */}
          <button title="Refresh"
            style={{ width: 32, height: 32, border: "none", borderRadius: "50%", background: "transparent", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: "#5f6368", transition: "background 0.12s", outline: "none" }}
            onMouseEnter={(e) => { e.currentTarget.style.background = "#f1f3f4" }}
            onMouseLeave={(e) => { e.currentTarget.style.background = "transparent" }}
          >
            <RefreshCw size={14} strokeWidth={1.8} />
          </button>

          <span style={{ fontSize: 13, fontWeight: 500, color: T.textTertiary, fontFamily: MONO, padding: "0 4px" }}>
            {filteredConversations.length === 0 ? "0" : `${pageStart + 1}–${pageEnd} of ${filteredConversations.length}`}
          </span>

          {paginationBtn(ChevronLeft,  page <= 1,          () => setPage((p) => Math.max(1, p - 1)))}
          {paginationBtn(ChevronRight, page >= totalPages,  () => setPage((p) => Math.min(totalPages, p + 1)))}
        </div>
      </div>

      {/* ── Rows ────────────────────────────────────────────────────── */}
      <div style={{ flex: 1, overflowY: "auto", background: T.bg }}>
        {loadingConversations ? (
          <RowShimmer />
        ) : filteredConversations.length === 0 ? (
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "80px 0", textAlign: "center" }}>
            <div style={{ width: 44, height: 44, borderRadius: T.radiusCard, border: T.border, background: T.bgMuted, display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 16, boxShadow: T.shadow }}>
              <MailOpen size={18} color="#d1d5db" />
            </div>
            <p style={{ fontSize: 13, fontWeight: 700, color: T.text, margin: "0 0 6px", fontFamily: FF, letterSpacing: "-0.01em" }}>
              {searchQuery ? "No matches found" : "No messages yet"}
            </p>
            <p style={{ fontSize: 14, fontWeight: 500, color: T.textTertiary, margin: 0, fontFamily: FF }}>
              {searchQuery ? "Try a different search term" : "Your conversations will appear here"}
            </p>
          </div>
        ) : (
          pageItems.map((conv) => (
            <ConversationRow
              key={conv.id}
              conversation={conv}
              isInOrganization={isInOrganization}
              organizationId={organizationId}
              userId={userId}
              onClick={() => onSelectConversation(conv)}
            />
          ))
        )}
      </div>
    </>
  )
}

export default EmailThreads